import {deleteCategory, queryCategories} from '@wix/ambassador-events-v1-category/http'
import {
  MA_APP_IDS,
  installRegisteredApps,
  isMembersAreaInstalled,
  maybeInstallMembersArea,
  withMembersArea,
} from '@wix/members-area-integration-kit'
import type {AppExposedApis, EditorPlatformApp, EditorSDK} from '@wix/platform-editor-sdk'
import {
  EVENTS_MEMBERS_WIDGET_ID,
  EVENT_DETAILS_SECTION_ID,
  MEMBERS_APP_ID,
  NAVIGATION_ROUTE,
  SCHEDULE_SECTION_ID,
} from '@wix/wix-events-commons-statics'
import type {EditorReadyFn} from '@wix/yoshi-flow-editor'
import {getAppManifestFactory} from './editor-script/app-manifest/app-manifest'
import {handleAppInstalled, handleMigrate, installPage} from './editor-script/editor-action-handlers'
import {onEventFactory} from './editor-script/events'
import {ensurePagesManaged} from './editor-script/pages-panel'
import {WithApproval, createWithApproval} from './editor-script/services/concurrent-editing'
import {SdkWrapper} from './editor-script/services/sdk'
import {TFunction, getTranslateFunction} from './editor-script/services/translations'
import {parseStaticsUrlFromEditorScriptUrl} from './editor-script/services/url'
import {FlowApi} from './editor-script/types'

export const Interactions = {
  GET_TRANSLATIONS: 'get-translations',
  INSTALL_AND_MANAGE_PAGES: 'install-and-manage-pages',
  SET_APP_API: 'set-app-api',
  INSTALL_MEMBERS_AREA: 'install-members-area',
}

let editorSdk: EditorSDK
let sdkWrapper: ReturnType<typeof SdkWrapper>
let token: string
let t: TFunction
let forcedPageInstall: boolean
let staticsUrl: string
let locale: string
let disableMembersInstall: boolean
let withApproval: WithApproval
let flowApi: FlowApi
let responsive: boolean

const pageInstallOrigins = new Set(['SITE_CREATION', 'SITE_GENERATION', 'ADD_SECTION_PANEL', 'ADD_PAGE_PANEL'])

interface EventsAppInterface extends Omit<EditorPlatformApp, 'editorReady'> {
  editorReady: EditorReadyFn
}

const EventsApp: EventsAppInterface = {
  editorReady: async (_editorSdk, _token, options, _flowApi) => {
    editorSdk = _editorSdk
    token = _token
    flowApi = _flowApi
    sdkWrapper = SdkWrapper(editorSdk, token)
    const origin = options?.origin?.info?.type

    locale = flowApi.environment.userLanguage
    staticsUrl = parseStaticsUrlFromEditorScriptUrl(options.initialAppData.editorScriptUrl)
    flowApi.fedops.interactionStarted(Interactions.GET_TRANSLATIONS)
    t = await getTranslateFunction(staticsUrl, locale, flowApi.httpClient)
    flowApi.fedops.interactionEnded(Interactions.GET_TRANSLATIONS)
    forcedPageInstall = pageInstallOrigins.has(origin)
    disableMembersInstall = origin === 'SITE_CREATION'
    responsive = options?.origin?.type === 'RESPONSIVE'
    withApproval = createWithApproval(editorSdk, token, flowApi)

    flowApi.fedops.interactionStarted(Interactions.INSTALL_AND_MANAGE_PAGES)
    await withApproval(async () => {
      await installPage({
        editorSdk,
        token,
        pageId: EVENT_DETAILS_SECTION_ID,
        title: t('pagesPanelEventsDetailsTitle'),
        forcedPageInstall,
      })
      await installPage({
        editorSdk,
        token,
        pageId: SCHEDULE_SECTION_ID,
        title: t('pagesPanelEventsScheduleTitle'),
        forcedPageInstall,
      })
      await ensurePagesManaged(editorSdk, token, t)
    })
    flowApi.fedops.interactionEnded(Interactions.INSTALL_AND_MANAGE_PAGES)

    onWidgetRemoved()
    onDashboardVisited()

    flowApi.fedops.interactionStarted(Interactions.SET_APP_API)
    await setAppAPI()
    flowApi.fedops.interactionEnded(Interactions.SET_APP_API)
  },

  exports: (_editorSdk): AppExposedApis => ({
    editor: {
      migrate: payload => {
        handleMigrate({
          payload,
          withApproval,
          editorSdk: _editorSdk,
          token,
          t,
          forcedPageInstall,
        })
      },
      appInstalled: payload =>
        handleAppInstalled({
          appDefinitionId: payload?.appDefinitionId,
          disableMembersInstall,
          withApproval,
          flowApi,
        }),
    },
  }),
  getAppManifest: getAppManifestFactory(
    () => t,
    () => locale,
    () => token,
    () => editorSdk,
    () => flowApi,
    () => responsive,
  ),
  onEvent: onEventFactory(
    () => token,
    () => flowApi,
  ),
}

const setAppAPI = async () => {
  const api: EventsApi = {
    subscribeToRevisionChanged: (): void => null,
    installMembersArea: async () => withApproval(installMembersArea),
    installMembersAreaSections: async () => withApproval(installMembersAreaSections),
    isMembersAreaInstalled,
    isEventsInMembersInstalled,
    navigateToEventsRoute,
  }
  await editorSdk.editor.setAppAPI(token, api)
}

interface MembersEditorApi {
  isAppSectionInstalled: (props: {widgetId: string}) => Promise<boolean>
}

const getMembersAreaEditorApi = (): Promise<MembersEditorApi> =>
  editorSdk.document.application.getPublicAPI('', {
    appDefinitionId: MEMBERS_APP_ID,
  }) as Promise<MembersEditorApi>

const isEventsInMembersInstalled = async () => {
  try {
    const membersEditorApi = await getMembersAreaEditorApi()

    return membersEditorApi.isAppSectionInstalled({widgetId: EVENTS_MEMBERS_WIDGET_ID})
  } catch (e) {
    // always throws on no api
  }

  return false
}

const installMembersArea = async () => {
  await maybeInstallMembersArea()
}

const installMembersAreaSections = async () => {
  await installRegisteredApps()
}

const onWidgetRemoved = () => {
  editorSdk.addEventListener('componentDeleted', async params => {
    const deletedCompId = params.detail?.componentRef?.id
    if (deletedCompId) {
      const {
        data: {categories = []},
      } = await flowApi.httpClient.request(
        queryCategories({
          query: {
            filter: {
              name: deletedCompId,
            },
            paging: {
              limit: 100,
            },
          },
        }),
      )
      await Promise.all(
        categories.map(category => {
          if (category.name === deletedCompId) {
            return flowApi.httpClient.request(deleteCategory({categoryId: category.id}))
          }
          return null
        }),
      )
    }
  })
}

const onDashboardVisited = () => {
  editorSdk.addEventListener('appVisitedInDashboard', () => {
    sdkWrapper.onDashboardClose()
  })
}

const navigateToEventsRoute = async (route: NAVIGATION_ROUTE) => {
  const tpaPageId = route === NAVIGATION_ROUTE.SCHEDULE ? SCHEDULE_SECTION_ID : EVENT_DETAILS_SECTION_ID
  await sdkWrapper.navigateToEventsPage(tpaPageId)

  if ([NAVIGATION_ROUTE.DETAILS, NAVIGATION_ROUTE.REGISTRATION].includes(route)) {
    await sdkWrapper.navigateWithinDetailsPage(route)
  }
}

const editorApp = withMembersArea(EventsApp, {
  installAutomatically: false,
  membersAreaApps: [MA_APP_IDS.ABOUT, MA_APP_IDS.MY_EVENTS, MA_APP_IDS.MY_WALLET],
})

export const editorReady = editorApp.editorReady
export const onEvent = editorApp.onEvent
export const getAppManifest = editorApp.getAppManifest
export const exports = editorApp.exports
